import CloseIcon from '@mui/icons-material/Close';
import { AppBar, Box, CircularProgress, IconButton, Toolbar, Typography } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { CameraDevice, CameraPicker } from '@components/CameraPicker';
import { DialogDarkFullscreen } from '@components/DialogDarkFullscreen';
import throttle from 'lodash/throttle';
import QrScanner from 'qr-scanner';

type QrScannerDialogProps = {
  open: boolean;
  title: string;
  onClose: VoidFunction;
  onQrCodeRecognized: (qrData: string) => void;
  loading?: boolean;
  disableCloseButton?: boolean;
};

const QrScannerDialog: FC<QrScannerDialogProps> = ({
  open,
  title,
  onClose,
  onQrCodeRecognized,
  loading = false,
  disableCloseButton = false,
}) => {
  const [cameraDevices, setCameraDevices] = useState<CameraDevice[]>([]);
  const [qrScanner, setQrScanner] = useState<QrScanner | null>(null);

  const throttledOnQrCodeRecognized = useMemo(
    () =>
      throttle((qrData: string) => {
        onQrCodeRecognized(qrData);
      }, 1000),
    [onQrCodeRecognized],
  );

  useEffect(() => {
    return () => throttledOnQrCodeRecognized.cancel();
  }, [throttledOnQrCodeRecognized]);

  useEffect(() => {
    if (!open) return;

    let isMounted = true;

    const initCamerasList = async () => {
      try {
        const camerasList = await QrScanner.listCameras();

        if (!isMounted) return;

        setCameraDevices(camerasList.map((camera) => ({ deviceId: camera.id, label: camera.label })));
      } catch (error) {
        console.error('Unable to fetch cameras list', error);
      }
    };

    void initCamerasList();

    return () => {
      isMounted = false;
    };
  }, [open]);

  useEffect(() => {
    if (!qrScanner) return;

    return () => {
      qrScanner.destroy();
    };
  }, [qrScanner]);

  const handleVideoRefChange = useCallback(
    (videoElement: HTMLVideoElement | null) => {
      if (!videoElement) {
        setQrScanner(null);
        return;
      }

      const scanner = new QrScanner(videoElement, (result) => {
        if (result) {
          throttledOnQrCodeRecognized(result);
        }
      });

      void scanner.start();
      setQrScanner(scanner);
    },
    [throttledOnQrCodeRecognized],
  );

  const handleCameraChange = (id: string) => {
    qrScanner?.setCamera(id);
  };

  const isVideoVisible = open && !loading;

  return (
    <DialogDarkFullscreen open={open}>
      <AppBar sx={{ position: 'relative' }}>
        <Toolbar>
          <IconButton color="inherit" disableRipple disabled={disableCloseButton} onClick={onClose}>
            <CloseIcon />
          </IconButton>

          <Typography sx={{ ml: 2, flex: 1, textAlign: 'left' }} variant="h6" component="div">
            {title}
          </Typography>
        </Toolbar>
      </AppBar>

      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1,
          overflow: 'auto',
        }}
      >
        {!isVideoVisible ? (
          <CircularProgress />
        ) : (
          <>
            <CameraPicker
              cameras={cameraDevices}
              onCameraChange={handleCameraChange}
              sx={{
                position: 'absolute',
                top: 20,
                right: 20,
              }}
            />

            <video
              ref={handleVideoRefChange}
              playsInline
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
              }}
            />
          </>
        )}
      </Box>
    </DialogDarkFullscreen>
  );
};

export default QrScannerDialog;
